import { DepositFiatPaymentMethod, FiatMethodCategory, WithdrawFiatPaymentMethod } from 'config/types/payment'
import Column from 'layout/Components/Column'
import { RowMiddle } from 'layout/Components/Row'
import { useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { breakpointMap } from 'theme/base'
import Box from 'UIKit/Box/Box'
import Image from 'UIKit/Image'
import { getFilterScore } from 'utils'
import PaymentMethodCard, {
  PaymentMethodCardContainer,
} from 'views/Payment/MobilePayment/components/FiatPaymentMethodItem'
import {
  EmptyCurrencyResponse,
  EmptyMessageEnum,
  PaymentAppSearch,
  PaymentFormControl,
  TextSelect,
} from 'views/Payment/MobilePayment/components/styles'
import {
  PaymentSelect,
  PaymentSelectTrigger,
  SelectContentInjectedProps,
  SelectScroll,
  StyledExpansitionIconContainer,
} from '../PaymentSelect'

type Method = DepositFiatPaymentMethod | WithdrawFiatPaymentMethod
type MethodPayment<T> = [string, T[]]

export const filterBanksSearch = <T extends Method>(
  banks: MethodPayment<T>[],
  searchString: string,
): MethodPayment<T>[] => {
  try {
    if (!searchString || !banks) return banks

    const result = banks
      .map(([paymentMethodType, paymentMethodList]) => {
        const paymentMethodAsset = FiatMethodCategory.find((method) =>
          method.methods.includes(Number(paymentMethodType)),
        )

        if (!paymentMethodAsset) return null

        const methodNameScore = getFilterScore(paymentMethodAsset.title, searchString)

        if (methodNameScore) {
          return [paymentMethodType, paymentMethodList] as MethodPayment<T>
        }

        const matchedBanks = paymentMethodList
          .map((item) => {
            const nameScore = getFilterScore(item.name, searchString)
            if (!nameScore) return null
            return { ...item, priority: nameScore }
          })
          .filter((item): item is T & { priority: number } => !!item)

        if (matchedBanks.length > 0) {
          const sortedBanks = matchedBanks
            .sort((a, b) => b.priority - a.priority)
            .map(({ priority: _, ...rest }) => rest as unknown as T)

          return [paymentMethodType, sortedBanks] as MethodPayment<T>
        }

        return null
      })
      .filter((item): item is MethodPayment<T> => !!item)

    return result
  } catch (e) {
    console.error(e)
    return banks
  }
}

export const BankFormSelect: React.FC<{
  value: Method
  onPresentMethodSelect: () => void
}> = ({ value, onPresentMethodSelect }) => {
  const { t } = useTranslation()

  return (
    <StyledPaymentFormControl
      state={{
        errors: [],
        value: '',
        isDirty: false,
      }}
      formatErrorMessage={() => ''}
      label={t('Payment Method')}
    >
      <PaymentSelectTrigger minHeight={44} onClick={onPresentMethodSelect}>
        {value ? (
          <RowMiddle gap="8px" id="token-select-item">
            <Box minWidth="28px">
              <Image src={value.thumbnail} width={28} height={26} />
            </Box>
            <TextSelect>{value.name}</TextSelect>
          </RowMiddle>
        ) : (
          <TextSelect placeholder>
            <Trans>Choose Payment Method</Trans>
          </TextSelect>
        )}
      </PaymentSelectTrigger>
    </StyledPaymentFormControl>
  )
}

export const ListBankSelect = <T extends Method>({
  banks,
  typedSearchBank,
  onSelect,
  maxHeight,
  selectedMethod,
}: {
  banks?: MethodPayment<T>[]
  typedSearchBank?: string
  onSelect: (value: T) => void
  maxHeight?: number | string
  selectedMethod: T
}) => {
  const options = useMemo(() => {
    return filterBanksSearch(banks, typedSearchBank)
  }, [banks, typedSearchBank])

  return (
    <SelectScroll maxHeight={maxHeight} gap="var(--pm-space-component, 20px)">
      {options.length > 0
        ? options?.map(([paymentMethodType, paymentMethodList]) => {
            const paymentMethodAsset = FiatMethodCategory.find((method) =>
              method.methods.includes(Number(paymentMethodType)),
            )

            if (!paymentMethodList || paymentMethodList.length === 0 || !paymentMethodAsset) return null

            return (
              <PaymentMethodCardContainer title={paymentMethodAsset.title} Icon={paymentMethodAsset.icon}>
                {paymentMethodList?.map((item) => (
                  <PaymentMethodCard selectedMethod={selectedMethod} method={item} onItemSelected={onSelect} />
                ))}
              </PaymentMethodCardContainer>
            )
          })
        : typedSearchBank && <EmptyCurrencyResponse message={EmptyMessageEnum.Bank} />}
    </SelectScroll>
  )
}

export const ContentBankSelect = <T extends Method>({
  onSelect,
  optionBanks = [],
  maxHeight,
  selectedMethod,
}: Partial<
  SelectContentInjectedProps<T> & {
    optionBanks?: MethodPayment<T>[]
    maxHeight?: number | string
    selectedMethod?: T
  }
>) => {
  const [searchOnlineBanking, setSearchingOnlineBanking] = useState('')
  const { t } = useTranslation()

  return (
    <Column gap="12px">
      <PaymentAppSearch
        className="app-search"
        placeholder={t('Local Bank Transfer (LBT)')}
        width="100%"
        value={searchOnlineBanking}
        onValueChanged={setSearchingOnlineBanking}
      />

      <ListBankSelect
        onSelect={onSelect}
        banks={optionBanks}
        maxHeight={maxHeight}
        typedSearchBank={searchOnlineBanking}
        selectedMethod={selectedMethod}
      />
    </Column>
  )
}

const BankSelectDropdown = <T extends Method>({
  options,
  onSelect,
  value,
  disabled,
}: {
  value: T
  options: MethodPayment<T>[]
  onSelect: (selectedItem: T) => void
  disabled?: boolean
}) => {
  const { t } = useTranslation()
  const RenderContent = useCallback(
    (props) => {
      return <ContentBankSelect maxHeight={300} optionBanks={options} {...props} />
    },
    [options],
  )

  return (
    <PaymentFormControl
      state={{
        errors: [],
        value: '',
        isDirty: false,
      }}
      formatErrorMessage={() => ''}
      label={t('Payment Method')}
      disabled={disabled}
    >
      <PaymentSelect<T>
        disabled={disabled}
        options={options as any}
        onSelect={onSelect}
        DropdownContent={RenderContent}
      >
        <PaymentSelectTrigger>
          {value ? (
            <RowMiddle gap="8px" id="token-select-item">
              <Box width={'34px'} height={'32px'} mr="5px">
                <Image src={value.thumbnail} width={32} height={30} />
              </Box>
              <TextSelect>{value.name}</TextSelect>
            </RowMiddle>
          ) : (
            <TextSelect placeholder>
              <Trans>Choose Payment Method</Trans>
            </TextSelect>
          )}
        </PaymentSelectTrigger>
      </PaymentSelect>
    </PaymentFormControl>
  )
}

const StyledPaymentFormControl = styled(PaymentFormControl)`
  @media screen and (max-width: ${breakpointMap.md}px) {
    ${StyledExpansitionIconContainer} {
      width: 12px;
      transform: rotate(0);
    }
  }
`

export default BankSelectDropdown
